import { useStaysListingAmenities } from '@/hooks/stays/useStaysListingAmenities'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { InfoIcon } from 'lucide-react'
import { useMemo } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'

interface FormCheckboxGroupProps<T extends FieldValues> {
  name: Path<T>
  label: string
  control: Control<T, any, any>
  isRequired?: boolean
}

export function FormCheckboxGroup<T extends FieldValues>({
  name,
  label,
  control,
  isRequired = false,
}: FormCheckboxGroupProps<T>) {
  const {
    field: { value: selectedIds, onChange }, // `value` is our string[] of selected IDs
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const { stListingAmenities } = useStaysListingAmenities()

  const handleCheckboxChange = (amenityId: string, checked: boolean) => {
    const currentIds = (selectedIds as string[]) || []
    if (!checked) {
      onChange(currentIds.filter((id) => id !== amenityId))
      return
    }

    onChange([...currentIds, amenityId])
  }
  const itemsCount = useMemo(() => selectedIds?.length || 0, [selectedIds])

  if (!stListingAmenities || stListingAmenities.length === 0) {
    return (
      <Box
        p={5}
        display="flex"
        justifyContent="center"
        alignItems="center"
        h="410px"
        w="full"
      >
        <Spinner size="xl" />
      </Box>
    )
  }

  return (
    <FormControl isInvalid={!!error} isRequired={isRequired}>
      <FormLabel className="flex gap-2 items-center">
        {label}
        <Tooltip
          hasArrow
          label="Para uma melhor performance do anúncio é recomendavel selecionar 8 amenities"
        >
          <InfoIcon size={'1rem'} className="text-green-500" />
        </Tooltip>
      </FormLabel>
      <Box
        borderWidth="1px"
        borderRadius="lg"
        w={'full'}
        h={'370px'}
        overflowY="auto"
      >
        <Accordion allowMultiple>
          {stListingAmenities.map((group) => (
            <AccordionItem key={group.title}>
              <h2>
                <AccordionButton _expanded={{ bg: 'gray.200' }}>
                  <Box
                    as="span"
                    flex="1"
                    textAlign="left"
                    fontWeight="semibold"
                  >
                    {group.title}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h2>
              <AccordionPanel pb={4}>
                <SimpleGrid
                  columns={{ base: 1, sm: 2, lg: 3 }}
                  spacingX={6}
                  spacingY={4}
                >
                  {group.items.map((amenity) => (
                    <Checkbox
                      key={amenity.id}
                      isChecked={((selectedIds || []) as string[]).includes(
                        amenity.stays_id
                      )}
                      onChange={({ target: { checked } }) => {
                        handleCheckboxChange(amenity.stays_id, checked)
                      }}
                      disabled
                    >
                      {amenity.name}
                    </Checkbox>
                  ))}
                </SimpleGrid>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Box>
      <Box
        className={`flex ${
          error?.message ? 'justify-between' : 'justify-end'
        } w-full pr-1 pt-1`}
      >
        <FormErrorMessage>{error?.message}</FormErrorMessage>
        <Text className={`text-xs ${itemsCount >= 8 ? 'text-green-500' : ''}`}>
          {itemsCount}/8
        </Text>
      </Box>
    </FormControl>
  )
}
